/**
 * Global ⌘K / Ctrl-K command palette (kit, R-13): a Medium kit modal holding a cmdk list. Owns its
 * own open state; the app supplies the items.
 */
import * as React from 'react';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';

/** Dispatch on window to open the palette (the L0 bar's search box). */
export const OPEN_PALETTE_EVENT = 'core:open-palette';

export interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon?: React.ReactNode;
  run: () => void;
}

export interface CommandPaletteProps {
  items: CommandItem[];
}

export function CommandPalette({ items }: CommandPaletteProps) {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    // #727: the L0 bar's "Find anything…" box opens the same palette
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  const groups = React.useMemo(() => {
    const byGroup = new Map<string, CommandItem[]>();
    for (const item of items) {
      const list = byGroup.get(item.group) ?? [];
      list.push(item);
      byGroup.set(item.group, list);
    }
    return Array.from(byGroup.entries());
  }, [items]);

  function runItem(item: CommandItem) {
    setOpen(false);
    item.run();
  }

  return (
    <BaseDialog.Root open={open} onOpenChange={(o: boolean) => setOpen(o)}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="k-scrim" />
        <BaseDialog.Popup className="k-modal k-palette" data-kit="modal" data-size="medium" aria-modal="true">
          <BaseDialog.Title className="sr-only">Command palette</BaseDialog.Title>
          <Command label="Command palette" className="flex max-h-[70vh] flex-col overflow-hidden bg-popover text-popover-foreground">
            <div className="flex items-center gap-2 border-b border-border px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <Command.Input
                autoFocus
                placeholder="Type a command or search…"
                className="k-palette-input w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
            <Command.List className="overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">No matching command</Command.Empty>
              {groups.map(([group, groupItems]) => (
                <Command.Group
                  key={group}
                  heading={group}
                  className="px-1 py-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[length:var(--t-eyebrow)] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
                >
                  {groupItems.map((item) => (
                    <Command.Item
                      key={item.id}
                      value={`${item.group} ${item.label} ${item.hint ?? ''}`}
                      onSelect={() => runItem(item)}
                      className="flex cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground transition-colors duration-150 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                    >
                      {item.icon}
                      <span className="flex-1">{item.label}</span>
                      {item.hint && <span className="text-[length:var(--t-th)] text-muted-foreground">{item.hint}</span>}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
